import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Solid color buttons (no gradients)
        default:
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Outline uses same base radius; hover fills with primary solid
        outline:
          'border-2 border-primary-blue text-primary-blue bg-transparent hover:bg-[hsl(var(--primary-blue))] hover:text-white hover:border-transparent transition-all duration-200',
        // Secondary uses lighter blue
        secondary:
          'bg-[hsl(var(--secondary-blue))] text-white hover:bg-[hsl(var(--primary-blue))] shadow-subtle',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',

        // Solid variants mapped to palette
        hero: 'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-elegant font-semibold',
        success:
          'bg-[hsl(var(--success))] text-white hover:opacity-90 shadow-subtle',
        info: 'bg-[hsl(var(--info))] text-white hover:opacity-90 shadow-subtle',
        warning:
          'bg-[hsl(var(--warning))] text-white hover:opacity-90 shadow-subtle',
        ai: 'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle border-0 font-medium',
        accent:
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle',
        professional:
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle',
        friendly:
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] rounded-xl shadow-subtle',
        // Pill alternatives
        'blue-pill':
          'bg-[hsl(var(--primary-blue))] text-white border border-white/30 rounded-full shadow-subtle',
        // Transparent, blue-bordered pill with blue text (hover fills solid)
        'blue-outline-pill':
          'border-2 border-primary-blue text-primary-blue bg-transparent rounded-full hover:bg-[hsl(var(--primary-blue))] hover:text-white hover:border-transparent transition-all duration-200',

        // Action-specific variants
        'generate-report':
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle font-medium rounded-xl',
        'view-details':
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle',
        'edit-data':
          'bg-[hsl(var(--primary-blue))] text-white hover:bg-[hsl(var(--secondary-blue))] shadow-subtle',
        'delete-item':
          'bg-danger-red text-white hover:bg-danger-red/90 shadow-subtle',
        'modern-outline':
          'border-2 border-primary-blue text-primary-blue hover:bg-[hsl(var(--primary-blue))] hover:text-white hover:border-transparent transition-all duration-200',
      },
      size: {
        default: 'px-3.5 py-2',
        sm: 'rounded-md px-3 py-1.5 text-xs',
        lg: 'rounded-md px-7 py-2.5 text-base font-medium',
        xl: 'rounded-lg px-10 py-3.5 text-lg font-semibold',
        icon: 'p-2.5',
        'icon-sm': 'p-2',
        'icon-lg': 'p-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
